import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div className={`fixed bottom-5 right-5 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium animate-fade-in
      ${type === 'success'
        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
        : 'bg-red-50 border-red-200 text-red-800'}`}>
      {type === 'success'
        ? <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        : <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
      }
      {message}
    </div>
  );
}

export default function AccountPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({ name: '', email: '' });
  const [profileLoading, setProfileLoading] = useState(false);

  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [toast, setToast] = useState(null);

  useEffect(() => {
    API.get('/landlord/me').then(({ data }) => {
      setProfile({ name: data.name, email: data.email });
    }).catch(() => navigate('/login'));
  }, [navigate]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profile.name.trim() || !profile.email.trim()) {
      return showToast('Name and email are required', 'error');
    }
    setProfileLoading(true);
    try {
      const { data } = await API.patch('/landlord/account', {
        name: profile.name.trim(),
        email: profile.email.trim().toLowerCase(),
      });
      // Update auth context so nav name reflects the change immediately
      const storedToken = localStorage.getItem("rf_token") || "";
      login(storedToken, { ...user, name: data.name, email: data.email });
      showToast('Profile updated');
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not update profile', 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (passwords.newPassword.length < 6) {
      return showToast('New password must be at least 6 characters', 'error');
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      return showToast('Passwords do not match', 'error');
    }
    setPasswordLoading(true);
    try {
      await API.patch('/landlord/account', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showToast('Password updated');
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not update password', 'error');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Account settings</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your name, email, and password</p>
        </div>

        {/* Profile section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-5">
          <h2 className="text-base font-semibold text-slate-800 mb-5">Profile</h2>
          <form onSubmit={handleProfileSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Full name</label>
              <input
                type="text"
                value={profile.name}
                onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                required
                className="form-input"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
              <input
                type="email"
                value={profile.email}
                onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
                required
                className="form-input"
                placeholder="you@example.com"
              />
            </div>
            <div className="pt-1">
              <button type="submit" disabled={profileLoading} className="btn-primary">
                {profileLoading ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>

        {/* Password section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-slate-800 mb-5">Change password</h2>
          <form onSubmit={handlePasswordSave} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Current password</label>
              <input
                type="password"
                value={passwords.currentPassword}
                onChange={e => setPasswords(p => ({ ...p, currentPassword: e.target.value }))}
                required
                autoComplete="current-password"
                className="form-input"
                placeholder="Your current password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
              <input
                type="password"
                value={passwords.newPassword}
                onChange={e => setPasswords(p => ({ ...p, newPassword: e.target.value }))}
                required
                autoComplete="new-password"
                className="form-input"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm new password</label>
              <input
                type="password"
                value={passwords.confirmPassword}
                onChange={e => setPasswords(p => ({ ...p, confirmPassword: e.target.value }))}
                required
                autoComplete="new-password"
                className="form-input"
                placeholder="Repeat new password"
              />
            </div>
            <div className="pt-1">
              <button type="submit" disabled={passwordLoading} className="btn-primary">
                {passwordLoading ? 'Updating...' : 'Update password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
